/**
 * 단기매매 도메인 타입.
 *
 * 백엔드 trading/dto 와 1:1로 대응한다.
 * 한쪽을 바꾸면 반드시 다른 쪽도 바꾼다.
 */

/** 채점 결과. 아직 평가 전이면 null. */
export type RecommendationResult = 'WIN' | 'LOSS' | 'OPEN_EXPIRED';

/** 지표. 백엔드 IndicatorResponse. 값의 의미는 docs/impl/14-technical-indicator.md */
export interface Indicator {
  baseDate: string;
  /** 14일 ATR. 하루 평균 가격 진폭 */
  atr14: number | null;
  /** ATR / 종가 × 100. 변동성 비율(%) */
  atrRatio: number | null;
  ma20: number | null;
  ma60: number | null;
  ma120: number | null;
  /** 종가 / 20일선 × 100. 100이면 이동평균과 같은 자리 */
  disparity20: number | null;
  /** 볼린저 %B. 0이면 하단선, 1이면 상단선 */
  bollingerPb: number | null;
  rsi14: number | null;
  /** 최근 레인지에서의 위치. 0이면 저점, 1이면 고점 */
  rangePosition: number | null;
  /** 20일 평균 거래대금 */
  avgTradeAmount: number | null;
}

/** LLM 분석의 근거가 된 출처. 없으면 분석 자체가 폐기된다 (CLAUDE.md 1.3절) */
export interface AnalysisSource {
  title: string;
  url: string;
}

/**
 * LLM 분석. 백엔드 StockAnalysis.
 *
 * 🔴 목표가·손절가·점수·도달확률은 여기 없다.
 *   그것들은 지표 계산과 백테스트가 만든 숫자다. LLM 이 만들지 않는다.
 */
export interface StockAnalysis {
  summary: string;
  positives: string[];
  negatives: string[];
  sources: AnalysisSource[];
  /** 자료의 충분성. sources 가 비면 LOW */
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
}

/** 수집된 뉴스·공시 한 건. 백엔드 SourceResponse */
export interface Source {
  id: number;
  title: string;
  url: string;
  publisher: string | null;
  /** NEWS | DISCLOSURE */
  sourceType: string;
  publishedAt: string | null;
}

/**
 * 추천 한 건. 백엔드 RecommendationResponse.
 *
 * 🔴 hitProbability 는 "수익 확률" 이 아니다.
 *   백테스트로 산출한 **과거 도달 빈도**다. 예측이 아니다. (CLAUDE.md 1.3절)
 *   sampleSize 를 항상 함께 보여줘야 한다 — 38건의 64% 와 500건의 64% 는 다르다.
 */
/**
 * 추천 프로필. 백엔드 RecommendationProfile (17단계 → 2026-08-20 재설계)
 *
 * 🔴 세 프로필의 진입 기준이 완전히 같다. 다른 것은 목표·손절 배수뿐이다
 *   같은 날 세 프로필에 같은 종목이 뜨는 것이 정상이다 - 모의투자가
 *   순수하게 출구 전략만 비교하게 하기 위한 의도된 설계다.
 */
export type TradingProfile = 'SAFE' | 'MODERATE' | 'AGGRESSIVE';

export interface Recommendation {
  id: number;
  /** 시세 조회용. 현재가·등락률 표시가 쓴다 (2026-08-28) */
  stockId: number;
  symbol: string;
  name: string;
  recommendedAt: string;
  /** SAFE | AGGRESSIVE. 목표·손절 배수와 도달률의 의미를 정한다 (17단계) */
  profile: TradingProfile;
  /** 화면에 그대로 쓰는 한국어 이름. 서버가 내려준다 */
  profileLabel: string;
  /** 0~100 */
  score: number;

  entryPrice: number;
  targetPrice: number;
  stopLossPrice: number;
  targetPercent: number;
  stopLossPercent: number;

  /** 과거 도달 빈도(%). 백테스트 표본이 없으면 null */
  hitProbability: number | null;
  /** 위 확률을 계산한 표본 수 */
  sampleSize: number | null;

  indicators: Indicator | null;
  analysis: StockAnalysis | null;
  sources: Source[];

  /** 채점 예정일 */
  evaluateOn: string | null;
  evaluatedAt: string | null;
  result: RecommendationResult | null;
  resultPrice: number | null;
  returnPercent: number | null;
  maxGainPercent: number | null;
  maxDrawdownPercent: number | null;
}

export interface JournalPage {
  items: Recommendation[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface CalibrationBucket {
  from: number;
  to: number;
  samples: number;
  actualWinRate: number | null;
}

export interface JournalStats {
  total: number;
  evaluated: number;
  pending: number;
  win: number;
  loss: number;
  expired: number;
  winRate: number | null;
  avgReturn: number | null;
  bestReturn: number | null;
  worstReturn: number | null;
  calibration: CalibrationBucket[];
}

/** 조회 기간 선택지. 백엔드가 90일까지만 허용한다 (MAX_RECENT_DAYS) */
export const PERIOD_OPTIONS = [
  { days: 7, label: '최근 7일' },
  { days: 30, label: '최근 30일' },
  { days: 90, label: '최근 90일' },
] as const;

/**
 * AI 분석 기준. 백엔드 AnalysisPolicyResponse. (3.12)
 *
 * 🔴 systemPrompt 는 실제로 모델에게 보내는 문자열 그대로다.
 *   화면에서 요약해 다시 쓰지 않는다. 다시 쓰면 프롬프트를 고쳤을 때
 *   그 요약이 남아 실제와 갈린다.
 */
export interface AnalysisPolicy {
  model: string;
  effort: string;
  systemPrompt: string;
  /** 프롬프트가 아니라 코드가 강제하는 규칙 */
  codeRules: string[];
}

/**
 * 기업행위(분할 · 병합) 의심 한 줄 — 관리자 › 모의투자 탭 (2026-10-11, V148 · CorpActionAdminService.Row)
 * 상태가 SUSPECT · CONFIRMED 면 그 종목은 모든 모의 계좌에서 매매가 멈춰 있다.
 */
export interface CorpActionSuspect {
  id: number;
  symbol: string | null;
  name: string | null;
  detectedOn: string;
  /** PRICE_JUMP(매매 직전 현재가가 튐) · CANDLE_REVISION(과거 일봉이 소급해 바뀜) · STALE_BASE(전일 종가가 낡았는데 현재가가 크게 다름) · BASE_RESET(장 전 기준가 재설정) · NO_LIMIT(보유 종목 상하한가 없음) */
  source: 'PRICE_JUMP' | 'CANDLE_REVISION' | 'STALE_BASE' | 'BASE_RESET' | 'NO_LIMIT';
  /** SUSPECT(정지 · 확인 대기) · CONFIRMED(기업행위 맞음 · 정지 유지) · FALSE_ALARM(오탐 · 재개) · ADJUSTED(비율 조정 적용 · 재개 — V149) */
  status: 'SUSPECT' | 'CONFIRMED' | 'FALSE_ALARM' | 'ADJUSTED';
  refPrice: number;
  observedPrice: number;
  ratio: number;
  factorGuess: string | null;
  candleDate: string | null;
  detectedAt: string;
  resolvedAt: string | null;
}

/** 기업행위 수동 비율 조정 입력 — "from 주 → to 주"(1주 → 5주 분할이면 from 1 · to 5). V149 · 1.5단계 */
export interface CorpActionAdjustRequest {
  symbol: string;
  effectiveDate: string;
  from: number;
  to: number;
}

/** 비율 조정이 바꾸는(바꾼) 행 하나 — 칸 이름 → 값. 막힌 미리보기면 after 가 null */
export interface CorpActionAdjustChange {
  table: string;
  id: number;
  accountId: number | null;
  before: Record<string, number | null>;
  after: Record<string, number | null> | null;
}

/** 미리보기 · 적용 결과. blockers 가 비어 있어야 적용된다 */
/** 유상증자 권리락 보정 입력 — 전일 종가 · 권리락 기준가(KRX). GPT [SPLIT-R3] Q5 */
export interface CorpActionRightsRequest {
  symbol: string;
  effectiveDate: string;
  prevClose: number;
  basePrice: number;
}

export interface CorpActionAdjustResult {
  symbol: string;
  name: string;
  /** SPLIT(분할 · 병합 · 무상 — from · to · factor = f) · RIGHTS(유상증자 권리락 — factor = k · prevClose · basePrice) */
  kind: 'SPLIT' | 'RIGHTS';
  effectiveDate: string;
  from: number;
  to: number;
  factor: number;
  prevClose: number | null;
  basePrice: number | null;
  blockers: string[];
  changes: CorpActionAdjustChange[];
  applied: boolean;
}
